import { getAdminClient } from "@/lib/supabase-admin";

export interface RankedItem {
  key: string;
  count: number;
}

export interface DailyPoint {
  day: string;
  views: number;
  sessions: number;
}

export interface AnalyticsTotals {
  totalViews: number;
  uniqueSessions: number;
  bounceRate: number;
}

export interface AnalyticsSummary extends AnalyticsTotals {
  trend: {
    totalViews: number;
    uniqueSessions: number;
    bounceRate: number;
  };
  daily: DailyPoint[];
  topPaths: RankedItem[];
  topReferrers: RankedItem[];
  topCountries: RankedItem[];
  utmSources: RankedItem[];
  utmMediums: RankedItem[];
  utmCampaigns: RankedItem[];
  deviceMix: RankedItem[];
  browserMix: RankedItem[];
  osMix: RankedItem[];
}

const TOP_LIMIT = 10;

interface PageViewSlim {
  ts: string;
  path: string | null;
  referrer: string | null;
  country: string | null;
  device_type: string | null;
  client_name: string | null;
  os_name: string | null;
  session_id: string | null;
  event_type: string;
  raw: Record<string, unknown> | null;
}

function pctChange(current: number, previous: number): number {
  if (previous === 0) return current === 0 ? 0 : 1;
  return (current - previous) / previous;
}

function isPageview(r: PageViewSlim): boolean {
  return r.event_type === "pageview" || r.event_type === "page_view";
}

function topN(
  items: PageViewSlim[],
  pick: (v: PageViewSlim) => string | null,
  fallback: string
): RankedItem[] {
  const counts = new Map<string, number>();
  for (const v of items) {
    const k = pick(v) ?? fallback;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Array.from(counts, ([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_LIMIT);
}

function extractUrlForUtm(r: PageViewSlim): string | null {
  if (r.raw && typeof r.raw === "object") {
    const origin = (r.raw as Record<string, unknown>).origin;
    if (typeof origin === "string") return origin;
    const url = (r.raw as Record<string, unknown>).url;
    if (typeof url === "string") return url;
  }
  return r.path;
}

function parseUtmParam(r: PageViewSlim, key: string): string | null {
  const candidate = extractUrlForUtm(r);
  if (!candidate) return null;
  const qIdx = candidate.indexOf("?");
  if (qIdx === -1) return null;
  try {
    const params = new URLSearchParams(candidate.slice(qIdx + 1));
    const value = params.get(key);
    return value && value.trim() !== "" ? value : null;
  } catch {
    return null;
  }
}

function computeTotals(rows: PageViewSlim[]): AnalyticsTotals {
  const views = rows.filter(isPageview);
  const totalViews = views.length;

  const sessions = new Set<string>();
  const sessionViewCount = new Map<string, number>();
  for (const v of views) {
    if (!v.session_id) continue;
    sessions.add(v.session_id);
    sessionViewCount.set(v.session_id, (sessionViewCount.get(v.session_id) ?? 0) + 1);
  }
  const uniqueSessions = sessions.size;

  let bounced = 0;
  for (const count of sessionViewCount.values()) {
    if (count === 1) bounced += 1;
  }
  const bounceRate = uniqueSessions === 0 ? 0 : bounced / uniqueSessions;

  return { totalViews, uniqueSessions, bounceRate };
}

function buildDaily(rows: PageViewSlim[], days: number, until: Date): DailyPoint[] {
  const dayKeys: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(until.getTime() - i * 24 * 60 * 60 * 1000);
    dayKeys.push(d.toISOString().slice(0, 10));
  }
  const viewsMap = new Map<string, number>();
  const sessionsMap = new Map<string, Set<string>>();
  for (const k of dayKeys) {
    viewsMap.set(k, 0);
    sessionsMap.set(k, new Set());
  }
  for (const v of rows.filter(isPageview)) {
    const day = v.ts.slice(0, 10);
    if (!viewsMap.has(day)) continue;
    viewsMap.set(day, (viewsMap.get(day) ?? 0) + 1);
    if (v.session_id) sessionsMap.get(day)!.add(v.session_id);
  }
  return dayKeys.map((day) => ({
    day,
    views: viewsMap.get(day) ?? 0,
    sessions: sessionsMap.get(day)?.size ?? 0,
  }));
}

export interface DateRange {
  from: Date;
  to: Date;
}

export function rangeFromDays(days: number): DateRange {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from, to };
}

export interface ActiveVisitors {
  activeSessions: number;
  activeViews: number;
  recentPaths: RankedItem[];
}

const ACTIVE_WINDOW_MS = 30 * 60 * 1000;

export async function fetchActiveVisitors(): Promise<ActiveVisitors> {
  const since = new Date(Date.now() - ACTIVE_WINDOW_MS).toISOString();
  const supabase = getAdminClient();
  const { data, error } = await supabase
    .from("page_views")
    .select("path, session_id, event_type")
    .gte("ts", since);
  if (error) throw error;

  const rows = (data ?? []) as Array<Pick<PageViewSlim, "path" | "session_id" | "event_type">>;
  const views = rows.filter((r) => r.event_type === "pageview" || r.event_type === "page_view");
  const sessions = new Set<string>();
  for (const v of views) if (v.session_id) sessions.add(v.session_id);

  const pathCounts = new Map<string, number>();
  for (const v of views) {
    const k = v.path ?? "(ukjent)";
    pathCounts.set(k, (pathCounts.get(k) ?? 0) + 1);
  }
  const recentPaths = Array.from(pathCounts, ([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    activeSessions: sessions.size,
    activeViews: views.length,
    recentPaths,
  };
}

/**
 * Henter to perioder (current + previous av samme lengde) for å beregne trender.
 * For pilot er JS-aggregering OK ved < 50k rader/måned. Bør flyttes til SQL hvis
 * volum vokser.
 */
export async function fetchAnalyticsSummary(range: DateRange): Promise<AnalyticsSummary> {
  const periodMs = range.to.getTime() - range.from.getTime();
  const days = Math.max(1, Math.ceil(periodMs / (24 * 60 * 60 * 1000)));
  const previousStart = new Date(range.from.getTime() - periodMs);

  const supabase = getAdminClient();
  const { data, error } = await supabase
    .from("page_views")
    .select("ts, path, referrer, country, device_type, client_name, os_name, session_id, event_type, raw")
    .gte("ts", previousStart.toISOString())
    .lte("ts", range.to.toISOString())
    .order("ts", { ascending: true });
  if (error) throw error;

  const rows = (data ?? []) as PageViewSlim[];
  const currentRows = rows.filter((r) => new Date(r.ts) >= range.from);
  const previousRows = rows.filter((r) => new Date(r.ts) < range.from);

  const totals = computeTotals(currentRows);
  const previous = computeTotals(previousRows);
  const trend = {
    totalViews: pctChange(totals.totalViews, previous.totalViews),
    uniqueSessions: pctChange(totals.uniqueSessions, previous.uniqueSessions),
    bounceRate: pctChange(totals.bounceRate, previous.bounceRate),
  };

  const views = currentRows.filter(isPageview);
  const daily = buildDaily(currentRows, days, range.to);

  const topPaths = topN(views, (v) => v.path, "(ukjent)");
  const topReferrers = topN(
    views,
    (v) => (v.referrer && v.referrer.trim() !== "" ? v.referrer : null),
    "(direkte)"
  );
  const topCountries = topN(views, (v) => v.country, "(ukjent)");
  const deviceMix = topN(views, (v) => v.device_type, "(ukjent)");
  const browserMix = topN(views, (v) => v.client_name, "(ukjent)");
  const osMix = topN(views, (v) => v.os_name, "(ukjent)");

  const utmSources = topN(views, (v) => parseUtmParam(v, "utm_source"), "(ingen)");
  const utmMediums = topN(views, (v) => parseUtmParam(v, "utm_medium"), "(ingen)");
  const utmCampaigns = topN(views, (v) => parseUtmParam(v, "utm_campaign"), "(ingen)");

  return {
    ...totals,
    trend,
    daily,
    topPaths,
    topReferrers,
    topCountries,
    utmSources,
    utmMediums,
    utmCampaigns,
    deviceMix,
    browserMix,
    osMix,
  };
}
